import { Component, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { skipWhile, take, tap } from 'rxjs/operators';

import { cloneDeep } from 'lodash';
import {
  ConfigurationState,
  MakeModelTrim,
  Odometer,
  ClearStockNumberList,
  ClearVinList,
  DoSearch,
  UpdateSearchCriteriaPayload
} from 'remarketing-angular-library';

import { AdvancedSearchService } from '../../../../../../search/services/advanced-search/advanced-search.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { TrapFocusComponent } from 'remarketing-ui-components-lib';
import { PopoverModule } from 'ngx-bootstrap/popover';
import {
  AdvancedSearchMakeComponent
} from '../advanced-search-make/advanced-search-make.component';
import {
  AdvancedSearchOdometerComponent
} from '../advanced-search-odometer/advanced-search-odometer.component';
import { CommonModule } from '@angular/common';
import {
  AdvancedSearchTrimComponent
} from '../advanced-search-trim/advanced-search-trim.component';
import {
  AdvancedSearchModelComponent
} from '../advanced-search-model/advanced-search-model.component';

@Component({
    selector: 'advanced-search-header',
    templateUrl: './advanced-search-header.component.html',
    styleUrls: ['./advanced-search-header.component.scss'],
    imports: [
        TranslocoDirective,
        TrapFocusComponent,
        PopoverModule,
        AdvancedSearchMakeComponent,
        AdvancedSearchOdometerComponent,
        CommonModule,
        AdvancedSearchTrimComponent,
        AdvancedSearchModelComponent
    ]
})
export class AdvancedSearchHeaderComponent implements OnInit {
  trimVisible = false;
  availableTrimCount: number;

  @ViewChild('classPop') classPopover;
  @ViewChild('makePop') makePopover;
  @ViewChild('modelPop') modelPopover;
  @ViewChild('trimPop') trimPopover;
  @ViewChild('distancePop') distancePopover;

  classOptionState$ = this.advancedSearchService.classOptions;
  makeOptionsState$ = this.advancedSearchService.makeOptions;
  modelOptionsState$ = this.advancedSearchService.modelOptions;
  trimOptionsState$ = this.advancedSearchService.trimOptions;

  tenantName = '';
  localSelecedMake: string[] = [];

  constructor(readonly store: Store, readonly advancedSearchService: AdvancedSearchService) {}

  ngOnInit() {
    this.advancedSearchService.getAdvancedSearchOptions();

    this.store
      .select(ConfigurationState.configuration)
      .pipe(
        skipWhile(configuration => !configuration),
        take(1),
        tap(({ tenantName }) => (this.tenantName = tenantName))
      )
      .subscribe();

    this.getAvailableTrimCount();
  }

  showMakeDropDown() {
    const makeOptionState = this.advancedSearchService._makeOptions.getValue();
    this.localSelecedMake = cloneDeep(makeOptionState.selectedMakeOptions.sort());
  }

  search() {
    const updateSearchCriteriaPayload = new UpdateSearchCriteriaPayload(
      this.getOdometerInfo(),
      this.getMakeModelTrims()
    );

    this.store.dispatch([new ClearVinList(), new ClearStockNumberList(), updateSearchCriteriaPayload, new DoSearch()]);
  }

  getMakeModelTrims(): MakeModelTrim[] {
    const makeModelTrims: MakeModelTrim[] = [];
    const makeOptionValue = this.advancedSearchService.makeOptionValue;
    const modelOptionValue = this.advancedSearchService.modelOptionValue;
    const trimOptionValue = this.advancedSearchService.trimOptionValue;

    let determinedMakes = [];
    if (makeOptionValue.selectedMakeOptions.length === 0 && modelOptionValue.selectedModelOptions.length > 0) {
      determinedMakes = this.determineMakesFromModels(modelOptionValue);
    } else {
      determinedMakes = makeOptionValue.selectedMakeOptions;
    }

    determinedMakes.forEach(make => {
      let vehicleModelsByMake;
      if (modelOptionValue && modelOptionValue.selectedModelOptions) {
        vehicleModelsByMake = modelOptionValue.selectedModelOptions.filter(model => model.vehicleMake === make);
      }

      if (vehicleModelsByMake && vehicleModelsByMake.length > 0) {
        vehicleModelsByMake.forEach(info => {
          const model = info.vehicleModel;
          let trims;

          if (trimOptionValue) {
            trims = this.getTrims(make, model, trimOptionValue.selectedTrimOptionsValues);
          }

          this.createAndAppendMakeModelTrim(makeModelTrims, make, model, trims);
        });
      } else {
        this.createAndAppendMakeModelTrim(makeModelTrims, make, undefined, undefined);
      }
    });
    return makeModelTrims;
  }

  createAndAppendMakeModelTrim(makeModelTrims: MakeModelTrim[], make: string, model: string, trims: string[]) {
    const makeModelTrim: MakeModelTrim = {
      make,
      model,
      trims
    };

    makeModelTrims.push(makeModelTrim);
  }

  determineMakesFromModels(modelOptionValue): any[] {
    const makeOptionValues = [];
    modelOptionValue.selectedModelOptions.forEach(model => {
      makeOptionValues.push(model.vehicleMake);
    });
    return makeOptionValues;
  }

  getTrims(make, model, selectedTrimOptionsValues: any[]) {
    let trims = [];
    if (selectedTrimOptionsValues && selectedTrimOptionsValues.length > 0) {
      trims = selectedTrimOptionsValues
        .filter(filteredOption => filteredOption.make === make && filteredOption.model === model)
        .map(filteredOption => filteredOption.trim);
    }

    return trims;
  }

  getOdometerInfo(): Odometer {
    const odometerValues = this.advancedSearchService.modelOdometerValue;

    if (odometerValues) {
      const min = odometerValues.min;
      const max = odometerValues.max;
      return {
        min: min > 0 ? min : undefined,
        max: max > 0 ? max : undefined
      };
    }

    return undefined;
  }

  getAvailableTrimCount() {
    this.trimOptionsState$.subscribe(trimOptionState => {
      let availableTrimCount = 0;
      trimOptionState.filteredTrimOptions.forEach(filteredTrimOption => {
        availableTrimCount += filteredTrimOption.vehicleTrims.length;
      });

      this.availableTrimCount = availableTrimCount;
    });
  }
}
